/** Read-only references to a linked section's Google Classroom stream (ADR 0037
 * decision 8). Classroom owns topics, materials, announcements and the
 * coursework made there before Mufid coursework moved into IMAM (ADR 0026 as
 * amended, option C); IMAM keeps
 * bounded references to them, refreshed on demand, and never treats them as
 * IMAM materials or sessions.
 */
import { api } from "@/lib/api/client";
import { commitOptions, retryOptions, type BulkResult } from "./commitSeams";
import type { CreateAssignment } from "./assignments";

export type ClassroomReferenceKind = "topic" | "material" | "announcement" | "coursework";
export type ClassroomReferenceReadState = "ready" | "empty" | "unavailable" | "access_denied";

export interface ClassroomReferenceDriveFile {
  file_id: string;
  title: string | null;
  alternate_link: string | null;
}

export interface ClassroomReference {
  kind: ClassroomReferenceKind;
  provider_id: string;
  title: string;
  excerpt: string | null;
  creator: { person_id: string | null; name: string | null };
  created_at: string | null;
  updated_at: string | null;
  topic_provider_id: string | null;
  drive_files: ClassroomReferenceDriveFile[];
  /** Staff responses only, on materials and announcements; null when Classroom
   * did not say, which keeps the item from learners. */
  audience?: "all_students" | "individual_students" | null;
}

export interface ClassroomReferences {
  /** The last successful read; null until one completes. */
  refreshed_at: string | null;
  /** The last read's outcome; null until the section is first read. */
  read_state: ClassroomReferenceReadState | null;
  items: ClassroomReference[];
}

export const classroomReferencesQueryKey = (courseId: string) =>
  ["courses", courseId, "classroom-references"] as const;

export const classroomReferencesApi = {
  get: (courseId: string) =>
    api.get<ClassroomReferences>(`/courses/${courseId}/classroom-references`),
  refresh: (courseId: string) =>
    api.post<ClassroomReferences>(`/courses/${courseId}/classroom-references/refresh`),
};

/** Posted in Classroom (board 6): the Classroom coursework of a course as one
 * read-only group, with the last read's facts and, for the teaching team, each
 * row's disposition and acts. The server decides every act; nothing here
 * reaches Classroom. */
export type PostedReadState = "known" | "not_read";
export type PostedAct = "reissue" | "close" | "try_again";
export type PostedDispositionKind = "reissued" | "closed" | "recorded";

export interface PostedTeachingFacts {
  assigned: number;
  turned_in: number;
  returned_graded: number;
  draft_graded: number;
}

/** A learner's own facts: what Classroom returned to them, never a draft grade. */
export interface PostedLearningFacts {
  state: "returned" | "turned_in" | "not_turned_in" | "none";
  turned_in_at: string | null;
  returned_at: string | null;
  grade: number | null;
}

interface PostedRowBase {
  provider_id: string;
  source: "history" | "reference";
  title: string;
  posted_at: string | null;
  due_at: string | null;
  points: number | null;
  /** Not read is unknown, never zero; `at` keeps the last successful read. */
  read: {
    state: PostedReadState;
    at: string | null;
    /** Why a row is not read: never read yet, the last read failed, or Classroom listed no submissions (unknown, never zero). */
    reason: "never_read" | "failed" | "no_submissions" | null;
    /**
     * A reference's known read more than a week old: its facts stand, settling
     * waits for a newer read. Never a kept record's: nothing reads it again,
     * so it settles from its own final read.
     */
    too_old: boolean;
  };
  /** The stored record's id in IMAM, the same id Coursework's record sheet returns focus to. */
  record_id: string | null;
  /** The stored record in IMAM, for a row the retired loop kept; never Classroom. */
  href: string | null;
  acts: PostedAct[];
}

export interface PostedTeachingRow extends PostedRowBase {
  facts: PostedTeachingFacts;
  disposition: { kind: PostedDispositionKind; at: string } | null;
  to_settle: boolean;
  prefill: { title: string; instructions: string | null; points: number | null } | null;
}

export interface PostedLearningRow extends PostedRowBase {
  facts: PostedLearningFacts;
}

interface PostedGroupBase {
  term_closed: boolean;
  read_at: string | null;
}

export type PostedInClassroom =
  | (PostedGroupBase & {
    perspective: "teaching";
    summary: { items: number; to_settle: number; not_read: number };
    rows: PostedTeachingRow[];
  })
  | (PostedGroupBase & {
    perspective: "learning";
    summary: { items: number; to_settle: null; not_read: number };
    rows: PostedLearningRow[];
  });

export type PostedDisposition =
  | { kind: "closed"; provider_ids: string[] }
  | { kind: "reissued"; provider_ids: [string]; assignment_id: string };

export type PostedDecision = ({ kind: "closed"; provider_ids: string[] } | { kind: "reissued"; provider_ids: [string]; native_draft: CreateAssignment }) & { retry_ids?: string[] };

export const postedInClassroomQueryKey = (courseId: string) =>
  ["courses", courseId, "classroom-coursework"] as const;

export const postedInClassroomApi = {
  get: (courseId: string) => api.get<PostedInClassroom>(`/courses/${courseId}/classroom-coursework`),
  decide: (courseId: string, body: PostedDecision, key: string, retry = false) =>
    api.post<BulkResult & { group: PostedInClassroom }>(`/courses/${courseId}/classroom-coursework/dispositions`, body, retry ? retryOptions(key) : commitOptions(key)),
  settle: (courseId: string, body: PostedDisposition) =>
    api.post<PostedInClassroom>(`/courses/${courseId}/classroom-coursework/dispositions`, body),
};
